import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useContracts, useClients, useTarifas, useComisionConfig, useConvenios, useAfianzadoras } from "../../contexts/DataContext";
import { Heading } from "../../components/heading";
import { Text } from "../../components/text";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/table";
import { Badge } from "../../components/badge";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { FianzaView } from "../../components/FianzaView";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  getPrimaAmount,
  getFechaEmision,
  calculateTarifa,
  getEmisorNombre,
  getSubRamo,
  getTipoFianza,
  toTimestamp,
  formatNumeroFianza,
} from "../../utils/calculations";
import { useComisionLedger } from "../../hooks/useComisionLedger";
import { indexMovementsByFianza, resolveComision } from "../../utils/comisionReconcile";
import { mapAfianzadoraName } from '../../utils/afianzadoras';

// Line colors for chart
const LINE_COLORS = [
  '#284865', // azul-surelia
  '#C2F40C', // verde-lima
  '#00D4FF', // cian-digital
  '#7c3aed', // purple
  '#f59e0b', // amber
  '#f43f5e', // rose
  '#14b8a6', // teal
  '#6366f1', // indigo
];

const monthNames = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

// Map long afianzadora names to short display names

export function ComisionPorAfianzadora({ selectedYear }) {
  const { contractsList, contractsLoading } = useContracts();
  const { clientsById, clientsLoading } = useClients();
  const { afianzadorasList } = useAfianzadoras();
  const { tarifas, tarifasLoading } = useTarifas();
  const { movements } = useComisionLedger();
  const { config: comisionConfig } = useComisionConfig();
  const { conveniosById } = useConvenios();
  const byFianza = useMemo(() => indexMovementsByFianza(movements), [movements]);
  const [selectedMonth, setSelectedMonth] = useState("all"); // "all" means whole year
  const [selectedEmisor, setSelectedEmisor] = useState(null);
  const [selectedFianza, setSelectedFianza] = useState(null); // For FianzaView

  const allFianzas = useMemo(() => {
    return contractsList.flatMap((contract) => {
      // Convenio-split context so the on-the-fly fallback nets the same way
      // the ledger books (see comisionReconcile.resolveComision).
      const splitOpts = {
        contractData: contract,
        orgConfig: comisionConfig,
        convenioData: contract.convenioId ? conveniosById?.[contract.convenioId] : null,
      };
      const clientName = clientsById[contract.clientId]?.nombreEmpresa || "Cliente sin nombre";
      const contratoTitle = contract.tituloContrato?.value || "Sin título";

      return (contract.fianzas || [])
        .filter((fianza) => fianza.estadoFianza !== 'anulada' && fianza.estadoFianza !== 'sustituida') // anulada + sustituida excluded; cancelada retained
        .map((fianza) => {
        // FIX: Use fechaAutorizacion instead of inicioVigenciaFianza for consistency
        const inicio = getFechaEmision(fianza);
        const montoAfianzado = Number(fianza.montoAfianzado) || 0;
        const vigencia = { inicio: toTimestamp(fianza.inicioVigenciaFianza), fin: toTimestamp(fianza.finVigenciaFianza) };
        const tarifaCalculada = calculateTarifa(getPrimaAmount(fianza), montoAfianzado, { vigencia });
        const emisorNombreRaw = getEmisorNombre(fianza);
        const emisorNombre = mapAfianzadoraName(emisorNombreRaw, afianzadorasList);
        const fianzaSubRamo = getSubRamo(fianza);
        const fianzaTipo = getTipoFianza(fianza);

        // Ledger-authoritative when the fianza has movements; on-the-fly otherwise.
        const { prima: primaAmount, comisionPct, comisionMonto, primaMinima, tarifa: fianzaTarifa } =
          resolveComision(fianza, byFianza.get(fianza.id), tarifas, emisorNombreRaw, fianzaSubRamo, fianzaTipo, splitOpts);

        return {
          ...fianza,
          clientId: contract.clientId,
          contratoId: contract.id,
          contratoTitle,
          clientName,
          inicioVigenciaMs: inicio,
          montoAfianzado,
          primaAmount,
          tarifaCalculada,
          emisorNombre,
          fianzaTarifa,
          primaMinima,
          comisionPct,
          comisionMonto,
        };
      });
    });
  }, [contractsList, clientsById, afianzadorasList, tarifas, byFianza, comisionConfig, conveniosById]);

  const filtered = useMemo(() => {
    return allFianzas.filter((f) => {
      if (!f.inicioVigenciaMs) return false;
      const date = new Date(f.inicioVigenciaMs);
      const yr = date.getFullYear();
      const month = date.getMonth();
      if (yr !== selectedYear) return false;
      if (selectedMonth !== "all" && month !== selectedMonth) return false;
      return true;
    });
  }, [allFianzas, selectedYear, selectedMonth]);

  const resumen = useMemo(() => {
    const map = new Map();
    filtered.forEach((f) => {
      const key = f.emisorNombre || "Sin emisor";
      const entry = map.get(key) || {
        emisor: key,
        monto: 0,
        prima: 0,
        comision: 0,
        count: 0,
      };
      entry.monto += Number(f.montoAfianzado) || 0;
      entry.prima += Number(f.primaAmount) || 0;
      entry.comision += Number(f.comisionMonto) || 0;
      entry.count += 1;
      map.set(key, entry);
    });
    return Array.from(map.values()).sort((a, b) => b.comision - a.comision);
  }, [filtered]);

  // Year-filtered fianzas (for chart, ignoring month filter)
  const yearFiltered = useMemo(() => {
    return allFianzas.filter((f) => {
      if (!f.inicioVigenciaMs) return false;
      const yr = new Date(f.inicioVigenciaMs).getFullYear();
      return yr === selectedYear;
    });
  }, [allFianzas, selectedYear]);

  // Get unique emisores for chart
  const emisoresList = useMemo(() => {
    const set = new Set();
    yearFiltered.forEach((f) => {
      if (f.emisorNombre) set.add(f.emisorNombre);
    });
    return Array.from(set).sort();
  }, [yearFiltered]);

  // Monthly data by afianzadora for chart
  const chartData = useMemo(() => {
    return monthNames.map((month, monthIndex) => {
      const dataPoint = {
        month: month.substring(0, 3).charAt(0).toUpperCase() + month.substring(1, 3),
      };
      emisoresList.forEach((emisor) => {
        const monthlyComision = yearFiltered
          .filter((f) => {
            const m = new Date(f.inicioVigenciaMs).getMonth();
            return m === monthIndex && f.emisorNombre === emisor;
          })
          .reduce((sum, f) => sum + (Number(f.comisionMonto) || 0), 0);
        dataPoint[emisor] = monthlyComision;
      });
      return dataPoint;
    });
  }, [yearFiltered, emisoresList]);

  const fianzasForEmisor = useMemo(() => {
    if (!selectedEmisor) return [];
    return filtered
      .filter((f) => (f.emisorNombre || "Sin emisor") === selectedEmisor)
      .sort((a, b) => (b.inicioVigenciaMs || 0) - (a.inicioVigenciaMs || 0));
  }, [filtered, selectedEmisor]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(value);

  const loading = contractsLoading || clientsLoading || tarifasLoading;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <Heading level={1} className="text-3xl font-bold text-azul-surelia">
            Comisión por afianzadora
          </Heading>
          <Badge color="blue">{selectedYear}</Badge>
        </div>
        <Text className="text-azul-surelia/70">
          Comisiones estimadas por afianzadora en el periodo seleccionado.
        </Text>
      </div>

      <div className="flex flex-wrap gap-3 items-center" data-pdf-hide>
        <div className="flex items-center gap-2">
          <Text className="text-sm text-azul-surelia/80">Mes</Text>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value === "all" ? "all" : Number(e.target.value))}
            className="text-sm border rounded-lg px-3 py-2"
          >
            <option value="all">Todo el año</option>
            {monthNames.map((name, idx) => (
              <option key={name} value={idx}>
                {name.charAt(0).toUpperCase() + name.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Yearly Chart */}
      {emisoresList.length > 0 && (
        <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-zinc-200">
            <Heading level={3} className="text-xl text-azul-surelia">
              Comisión mensual
            </Heading>
            <Text className="text-sm text-azul-surelia/60 mt-1">
              Comisión por afianzadora durante {selectedYear}
            </Text>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="month"
                  tick={{ fill: '#284865', fontSize: 12 }}
                  axisLine={{ stroke: '#d1d5db' }}
                />
                <YAxis
                  tickFormatter={(value) =>
                    new Intl.NumberFormat("es-MX", {
                      style: "currency",
                      currency: "MXN",
                      notation: "compact",
                      maximumFractionDigits: 1,
                    }).format(value)
                  }
                  tick={{ fill: '#284865', fontSize: 12 }}
                  axisLine={{ stroke: '#d1d5db' }}
                />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  }}
                  labelStyle={{ color: '#284865', fontWeight: 600 }}
                />
                <Legend wrapperStyle={{ paddingTop: '20px' }} />
                {emisoresList.map((emisor, idx) => (
                  <Line
                    key={emisor}
                    type="monotone"
                    dataKey={emisor}
                    name={emisor}
                    stroke={LINE_COLORS[idx % LINE_COLORS.length]}
                    strokeWidth={2}
                    dot={{ fill: LINE_COLORS[idx % LINE_COLORS.length], strokeWidth: 0, r: 4 }}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
          <Heading level={3} className="text-xl text-azul-surelia">
            Resumen por afianzadora
          </Heading>
          <Badge color="zinc">Período: {selectedMonth === "all" ? "Año completo" : monthNames[selectedMonth]} {selectedYear}</Badge>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHead className="bg-azul-surelia text-white">
              <TableRow>
                <TableHeader className="text-white">Afianzadora</TableHeader>
                <TableHeader className="text-white">Fianzas</TableHeader>
                <TableHeader className="text-white">Prima</TableHeader>
                <TableHeader className="text-white">Comisión</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan="5" className="py-8 text-center text-azul-surelia/70">
                    Cargando datos…
                  </TableCell>
                </TableRow>
              ) : resumen.length === 0 ? (
                <TableRow>
                  <TableCell colSpan="4" className="py-8 text-center text-azul-surelia/70">
                    No hay fianzas en este periodo.
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {resumen.map((row) => (
                    <TableRow
                      key={row.emisor}
                      className={`cursor-pointer transition-colors ${selectedEmisor === row.emisor ? "bg-azul-surelia/10" : "hover:bg-azul-surelia/5"}`}
                      onClick={() => setSelectedEmisor(selectedEmisor === row.emisor ? null : row.emisor)}
                    >
                      <TableCell className="font-medium text-zinc-900">{row.emisor}</TableCell>
                      <TableCell>{row.count}</TableCell>
                      <TableCell>
                        {new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(
                          row.prima
                        )}
                      </TableCell>
                      <TableCell>
                        {new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(
                          row.comision
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-azul-surelia/5 border-t-2 border-azul-surelia/20">
                    <TableCell className="font-bold text-azul-surelia">Total</TableCell>
                    <TableCell className="font-bold text-azul-surelia">
                      {resumen.reduce((sum, r) => sum + r.count, 0)}
                    </TableCell>
                    <TableCell className="font-bold text-azul-surelia">
                      {new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(
                        resumen.reduce((sum, r) => sum + r.prima, 0)
                      )}
                    </TableCell>
                    <TableCell className="font-bold text-azul-surelia">
                      {new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(
                        resumen.reduce((sum, r) => sum + r.comision, 0)
                      )}
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Slide-over panel for fianzas list - rendered via portal (left side) */}
      {selectedEmisor && createPortal(
        <div className="fixed inset-y-0 left-0 z-[60] w-72 flex flex-col bg-white shadow-xl border-r border-azul-surelia/20">
          {/* Header */}
          <div className="bg-azul-surelia px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-semibold text-white truncate">
                  {selectedEmisor}
                </h2>
                <p className="text-xs text-white/80">
                  {selectedMonth === "all"
                    ? `Año completo ${selectedYear}`
                    : `${monthNames[selectedMonth].charAt(0).toUpperCase() + monthNames[selectedMonth].slice(1)} ${selectedYear}`}
                </p>
              </div>
              <button
                onClick={() => setSelectedEmisor(null)}
                className="rounded-lg p-1.5 text-white/80 hover:bg-white/10 hover:text-white transition-colors ml-2"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-3">
            {fianzasForEmisor.length === 0 ? (
              <div className="text-center py-8 text-zinc-500 text-sm">
                No hay fianzas para esta afianzadora.
              </div>
            ) : (
              <div className="space-y-2">
                {fianzasForEmisor.map((fianza) => (
                  <div
                    key={fianza.id}
                    onClick={() => setSelectedFianza(fianza)}
                    className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 hover:border-azul-surelia/40 hover:bg-white cursor-pointer transition-all"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-azul-surelia truncate">
                          {formatNumeroFianza(fianza, "Sin número")}
                        </div>
                        <div className="text-xs text-zinc-500 truncate">
                          {fianza.clientName}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-1 text-xs">
                      <div>
                        <div className="text-zinc-500">Prima</div>
                        <div className="font-semibold text-zinc-900">{formatCurrency(fianza.primaAmount || 0)}</div>
                      </div>
                      <div>
                        <div className="text-zinc-500">Tarifa</div>
                        <div className="font-semibold text-zinc-800">
                          {fianza.fianzaTarifa !== null ? `${Number(fianza.fianzaTarifa).toFixed(2)}%` : "N/A"}
                          {fianza.primaMinima && (
                            <span className="ml-1 text-[10px] text-amber-600 font-medium">Prima mín.</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-zinc-500">Comisión</div>
                        <div className="font-semibold text-azul-surelia">
                          {fianza.comisionMonto !== null ? formatCurrency(fianza.comisionMonto || 0) : "N/A"}
                        </div>
                        {fianza.comisionPct !== null && (
                          <div className="text-[10px] text-azul-surelia/70">
                            {fianza.comisionPct}%
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Summary */}
                <div className="border-t border-zinc-200 pt-2 mt-3 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-600">{fianzasForEmisor.length} fianzas</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-600">Prima total</span>
                    <span className="font-semibold text-zinc-900">
                      {formatCurrency(fianzasForEmisor.reduce((sum, f) => sum + (f.primaAmount || 0), 0))}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-600">Comisión total</span>
                    <span className="font-semibold text-azul-surelia">
                      {formatCurrency(fianzasForEmisor.reduce((sum, f) => sum + (f.comisionMonto || 0), 0))}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* FianzaView full-screen component - rendered via portal with higher z-index */}
      {selectedFianza && createPortal(
        <div className="relative z-[70]">
          <FianzaView
            isOpen={!!selectedFianza}
            onClose={() => setSelectedFianza(null)}
            fianza={selectedFianza}
            clientData={clientsById[selectedFianza.clientId] || {}}
            contratoData={contractsList.find((c) => c.id === selectedFianza.contratoId) || {}}
          />
        </div>,
        document.body
      )}
    </div>
  );
}
